import type React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type NetworkTraceInfo,
	normaliseTraceInfo,
} from '@atlassian/search-client/network-trace-info';

import { TASK_NAMES } from '../events/task-names';
import {
	type FullPageSearchNetworkRefs,
	type FullPageSearchAsyncSearchRefs,
	onTaskAbort,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../events/vital-stats-events';

export type CreateFullPageSearchVitalStatsArgs = {
	searchSessionId: string | null | undefined;
	fireAnalyticsEvent: (event: any) => void;
	fullPageSearchTaskIdRef: React.MutableRefObject<string | null>;
	fullPageSearchNetworkRefs: FullPageSearchNetworkRefs;
	fullPageSearchAsyncSearchRefs: FullPageSearchAsyncSearchRefs;
	cacheAvailable: boolean;
	cacheExpected: boolean;
	isNetworkMode: boolean;
};

export type CreateFullPageSearchNetworkVitalStatsArgs = {
	searchSessionId: string | null | undefined;
	fireAnalyticsEvent: (event: any) => void;
	fullPageSearchNetworkRefs: FullPageSearchNetworkRefs;
};

/**
 * Creates callbacks for Full Page Search VitalStats events (UX + Network).
 *
 * Keeps the orchestration logic out of query-plan so setupSearchExecution doesn't grow unbounded.
 *
 * External behavior is preserved:
 * - Uses the same feature flags
 * - Uses the same task names
 * - Fires identical taskStart/taskAbort events with the same moreContext attributes
 */
export function createFullPageSearchNetworkVitalStats({
	searchSessionId,
	fireAnalyticsEvent,
	fullPageSearchNetworkRefs,
}: CreateFullPageSearchNetworkVitalStatsArgs): {
	onNetworkSuccess: (resultCount: number, traceInfo?: NetworkTraceInfo) => void;
	onNetworkFail: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => void;
	onNetworkAbort: (reason: 'unmounted' | 'superseded') => void;
} {
	const isEnabled = () =>
		Boolean(searchSessionId) && fg('rovo-search-vitalstats-fullpage-search-network');

	const getTimeTaken = (): number | undefined => {
		const startAt = fullPageSearchNetworkRefs.current.startAt;
		return startAt ? Math.max(0, performance.now() - startAt) : undefined;
	};

	return {
		onNetworkSuccess: (resultCount: number, traceInfo?: NetworkTraceInfo) => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchNetworkRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			// Update state BEFORE firing event (prevents duplicates under re-entrancy)
			fullPageSearchNetworkRefs.current.state = {
				...state,
				state: 'completed',
			};

			const tracingHeaders = fg('rovo-search-vitalstats-tracing-headers')
				? normaliseTraceInfo(traceInfo)
				: {};

			fireAnalyticsEvent(
				onTaskSuccess({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
					moreContext: {
						uxTaskId: state.uxTaskId,
						resultCount,
						timeTaken: getTimeTaken(),
						cacheAvailable: state.cacheAvailable,
						cacheExpected: state.cacheExpected,
						...tracingHeaders,
					},
				}),
			);
		},

		onNetworkFail: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchNetworkRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			// Update state BEFORE firing event (prevents duplicates under re-entrancy)
			fullPageSearchNetworkRefs.current.state = {
				...state,
				state: 'failed',
			};

			const tracingHeaders = fg('rovo-search-vitalstats-tracing-headers')
				? normaliseTraceInfo(traceInfo)
				: {};

			fireAnalyticsEvent(
				onTaskFail({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
					moreContext: {
						uxTaskId: state.uxTaskId,
						errorName,
						errorMessage,
						timeTaken: getTimeTaken(),
						cacheAvailable: state.cacheAvailable,
						cacheExpected: state.cacheExpected,
						...tracingHeaders,
					},
				}),
			);
		},

		onNetworkAbort: (reason: 'unmounted' | 'superseded') => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchNetworkRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			fullPageSearchNetworkRefs.current.state = {
				...state,
				state: 'aborted',
			};

			fireAnalyticsEvent(
				onTaskAbort({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
					moreContext: {
						uxTaskId: state.uxTaskId,
						reason,
						cacheAvailable: state.cacheAvailable,
						cacheExpected: state.cacheExpected,
					},
				}),
			);
		},
	};
}

export type CreateFullPageSearchAsyncSearchVitalStatsArgs = {
	searchSessionId: string | null | undefined;
	fireAnalyticsEvent: (event: any) => void;
	fullPageSearchAsyncSearchRefs: FullPageSearchAsyncSearchRefs;
};

/**
 * Creates callbacks for Full Page Search Async Search VitalStats events.
 *
 * Tracks whether aggregator served the search response from its backend
 * cache after an async search query was made that expected a cached result.
 * Only fires when cacheExpected=true (a preload was attempted during SSR/QF).
 *
 * - onAsyncSearchSuccess: fires taskSuccess when isCachedResult=true (aggregator cache hit)
 * - onAsyncSearchFail: fires taskFail when isCachedResult=false (aggregator cache miss)
 * - onAsyncSearchAbort: fires taskAbort when task is superseded or unmounted
 */
export function createFullPageSearchAsyncSearchVitalStats({
	searchSessionId,
	fireAnalyticsEvent,
	fullPageSearchAsyncSearchRefs,
}: CreateFullPageSearchAsyncSearchVitalStatsArgs): {
	onAsyncSearchSuccess: () => void;
	onAsyncSearchFail: () => void;
	onAsyncSearchAbort: (reason: 'unmounted' | 'superseded') => void;
} {
	const isEnabled = () => Boolean(searchSessionId);

	const getTimeTaken = (): number | undefined => {
		const startAt = fullPageSearchAsyncSearchRefs.current.startAt;
		return startAt ? Math.max(0, performance.now() - startAt) : undefined;
	};

	return {
		onAsyncSearchSuccess: () => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchAsyncSearchRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			fullPageSearchAsyncSearchRefs.current.state = {
				...state,
				state: 'completed',
			};

			fireAnalyticsEvent(
				onTaskSuccess({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_ASYNC_SEARCH_RESULTS,
					moreContext: {
						uxTaskId: state.uxTaskId,
						isCachedResult: true,
						timeTaken: getTimeTaken(),
					},
				}),
			);
		},

		onAsyncSearchFail: () => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchAsyncSearchRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			fullPageSearchAsyncSearchRefs.current.state = {
				...state,
				state: 'failed',
			};

			fireAnalyticsEvent(
				onTaskFail({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_ASYNC_SEARCH_RESULTS,
					moreContext: {
						uxTaskId: state.uxTaskId,
						isCachedResult: false,
						timeTaken: getTimeTaken(),
					},
				}),
			);
		},

		onAsyncSearchAbort: (reason: 'unmounted' | 'superseded') => {
			if (!isEnabled()) {
				return;
			}

			const state = fullPageSearchAsyncSearchRefs.current.state;
			if (state.state !== 'started' || !state.taskId) {
				return;
			}

			fullPageSearchAsyncSearchRefs.current.state = {
				...state,
				state: 'aborted',
			};

			fireAnalyticsEvent(
				onTaskAbort({
					searchSessionId: searchSessionId ?? '',
					taskId: state.taskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_ASYNC_SEARCH_RESULTS,
					moreContext: {
						uxTaskId: state.uxTaskId,
						reason,
					},
				}),
			);
		},
	};
}

export function createFullPageSearchVitalStats({
	searchSessionId,
	fireAnalyticsEvent,
	fullPageSearchTaskIdRef,
	fullPageSearchNetworkRefs,
	fullPageSearchAsyncSearchRefs,
	cacheAvailable,
	cacheExpected,
	isNetworkMode,
}: CreateFullPageSearchVitalStatsArgs): {
	/**
	 * Called when a new search is initiated.
	 * Handles:
	 * - UX taskStart
	 * - Network taskAbort (superseded)
	 * - Network taskStart
	 * - Async search taskAbort (superseded)
	 * - Async search taskStart (only when cacheExpected=true)
	 */
	onSearchStart: () => `${string}-${string}-${string}-${string}-${string}`;
} {
	const startUxTask = (uxTaskId: string) => {
		fullPageSearchTaskIdRef.current = uxTaskId;

		if (searchSessionId) {
			fireAnalyticsEvent(
				onTaskStart({
					searchSessionId,
					taskId: uxTaskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_UX,
					moreContext: {
						cacheExpected,
					},
				}),
			);
		}
	};

	const abortSupersededNetworkTaskIfNeeded = () => {
		if (!searchSessionId || !isNetworkMode) {
			return;
		}

		createFullPageSearchNetworkVitalStats({
			searchSessionId,
			fireAnalyticsEvent,
			fullPageSearchNetworkRefs,
		}).onNetworkAbort('superseded');
	};

	const abortSupersededAsyncSearchTaskIfNeeded = () => {
		if (!searchSessionId || !isNetworkMode) {
			return;
		}

		createFullPageSearchAsyncSearchVitalStats({
			searchSessionId,
			fireAnalyticsEvent,
			fullPageSearchAsyncSearchRefs,
		}).onAsyncSearchAbort('superseded');
	};

	const startNetworkTask = (uxTaskId: string) => {
		if (
			!searchSessionId ||
			!isNetworkMode ||
			!fg('rovo-search-vitalstats-fullpage-search-network')
		) {
			return;
		}

		const networkTaskId = crypto.randomUUID();
		fullPageSearchNetworkRefs.current.taskId = networkTaskId;
		fullPageSearchNetworkRefs.current.startAt = performance.now();

		// Update state BEFORE firing event
		fullPageSearchNetworkRefs.current.state = {
			taskId: networkTaskId,
			uxTaskId, // correlate with UX task
			cacheAvailable,
			cacheExpected,
			state: 'started',
		};

		fireAnalyticsEvent(
			onTaskStart({
				searchSessionId,
				taskId: networkTaskId,
				task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_NETWORK,
				moreContext: {
					uxTaskId,
					cacheAvailable,
					cacheExpected,
				},
			}),
		);
	};

	const startAsyncSearchTask = (uxTaskId: string) => {
		if (!searchSessionId || !isNetworkMode || !cacheExpected) {
			return;
		}

		const asyncSearchTaskId = crypto.randomUUID();
		fullPageSearchAsyncSearchRefs.current.taskId = asyncSearchTaskId;
		fullPageSearchAsyncSearchRefs.current.startAt = performance.now();

		// Update state BEFORE firing event
		fullPageSearchAsyncSearchRefs.current.state = {
			taskId: asyncSearchTaskId,
			uxTaskId,
			cacheExpected,
			state: 'started',
		};

		fireAnalyticsEvent(
			onTaskStart({
				searchSessionId,
				taskId: asyncSearchTaskId,
				task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_ASYNC_SEARCH_RESULTS,
				moreContext: {
					uxTaskId,
					cacheExpected,
				},
			}),
		);
	};

	return {
		/**
		 * Called when a new search is initiated.
		 * Handles:
		 * - UX taskStart
		 * - Network taskAbort (superseded)
		 * - Network taskStart
		 * - Async search taskAbort (superseded)
		 * - Async search taskStart (only when cacheExpected=true)
		 */
		onSearchStart: () => {
			const uxTaskId = crypto.randomUUID();
			startUxTask(uxTaskId);

			abortSupersededNetworkTaskIfNeeded();
			startNetworkTask(uxTaskId);

			abortSupersededAsyncSearchTaskIfNeeded();
			startAsyncSearchTask(uxTaskId);

			return uxTaskId;
		},
	};
}
